import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Briefcase, MapPin, Award, Calendar, FileText } from 'lucide-react';
import { getJobSeekerProfiles as getFirebaseProfiles } from '../lib/firebase/jobSeekers';
import { getJobSeekerProfiles as getSupabaseProfiles } from '../lib/supabase/jobSeekers';
import Button from '../components/common/Button';
import type { JobSeekerProfile } from '../types/profiles';

export default function JobSeekerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<JobSeekerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProfile() {
      if (!id) return;
      
      try {
        // Try to find the profile in both Firebase and Supabase
        const [firebaseResult, supabaseResult] = await Promise.all([
          getFirebaseProfiles(),
          getSupabaseProfiles()
        ]);
        
        // Combine profiles from both sources
        const allProfiles = [
          ...firebaseResult.profiles,
          ...supabaseResult.profiles
        ];
        
        // Find the specific profile by ID
        const foundProfile = allProfiles.find(p => p.id === id);
        
        if (!foundProfile) {
          setError('Profile not found');
          return;
        }
        
        setProfile(foundProfile);
      } catch (err) {
        setError('Failed to load profile details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error || 'Profile not found'}</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/job-seekers')}
          >
            Back to Job Seekers
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link
        to="/job-seekers"
        className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Job Seekers
      </Link>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Job Seeker Profile</h1>
            <p className="text-gray-600 mt-2">{profile.about}</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-green-600">
              ${profile.desiredPay}/hr
            </div>
            <div className="text-sm text-gray-500 capitalize">
              {profile.availability} availability
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Briefcase className="h-5 w-5 text-green-600" />
                <h2 className="text-lg font-semibold">Looking For</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.jobTypes.map((type, i) => (
                  <span key={i} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm capitalize">
                    {type}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center space-x-2 mb-3">
                <MapPin className="h-5 w-5 text-green-600" />
                <h2 className="text-lg font-semibold">Preferred Locations</h2>
              </div>
              <div className="space-y-2">
                {profile.locations.map((location, i) => (
                  <div key={i} className="text-gray-700">{location}</div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Award className="h-5 w-5 text-green-600" />
                <h2 className="text-lg font-semibold">Skills & Experience</h2>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {profile.skills.map((skill, i) => (
                  <span key={i} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                    {skill}
                  </span>
                ))}
              </div>
              <p className="text-gray-700">
                Experience Level: <span className="font-medium capitalize">{profile.experience}</span>
              </p>
            </div>

            {profile.certifications.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <Calendar className="h-5 w-5 text-green-600" />
                  <h2 className="text-lg font-semibold">Certifications</h2>
                </div>
                <div className="space-y-2">
                  {profile.certifications.map((cert, i) => (
                    <div key={i} className="text-gray-700">{cert}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {profile.email && (
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Email</h3>
                <p className="text-gray-600">{profile.email}</p>
              </div>
            )}
            {profile.phone && (
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Phone</h3>
                <p className="text-gray-600">{profile.phone}</p>
              </div>
            )}
          </div>
        </div>

        {/* Resume Section */}
        {profile.resumeUrl && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-xl font-semibold mb-4">Resume</h2>
            <a
              href={profile.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-green-600 hover:text-green-700 font-medium text-lg"
            >
              <FileText className="h-6 w-6" />
              <span>View Resume</span>
            </a>
          </div>
        )}

        {/* Voice Introduction Section */}
        {profile.voiceIntroUrl && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-xl font-semibold mb-4">Voice Introduction</h2>
            <audio
              controls
              className="w-full max-w-2xl"
            >
              <source src={profile.voiceIntroUrl} type="audio/webm" />
              Your browser does not support the audio element.
            </audio>
          </div>
        )}

        {/* Video Introduction Section */}
        {profile.videoUrl && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-xl font-semibold mb-4">Video Introduction</h2>
            <video
              controls
              className="w-full max-w-2xl rounded-xl shadow-lg"
              preload="metadata"
            >
              <source src={profile.videoUrl} type="video/mp4" />
              <source src={profile.videoUrl} type="video/webm" />
              <source src={profile.videoUrl} type="video/quicktime" />
              Your browser does not support the video tag.
            </video>
          </div>
        )}
      </div>
    </div>
  );
}